import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import { formatDate, formatDateTime } from '@/lib/format';
import { InvoiceStatusBadge } from '@/components/InvoiceStatusBadge';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { PaymentStatusBadge } from '@/components/PaymentStatusBadge';
import type { Invoice, Payment } from '@etiquetachef/types';
import {
  subscribeTenantInvoices,
  subscribeTenantPayments,
  subscribeTenantRefunds,
  type InvoiceListResult,
  type PaymentListResult,
  type RefundListResult,
} from '@/lib/firestoreTenantDetail';
import { RefundsTable } from './RefundsTable';

type Props = {
  tenantId: string;
};

/**
 * Aba "Histórico financeiro" do detalhe de tenant: últimas 10 faturas, 10 pagamentos e os
 * reembolsos do tenant.
 *
 * Origem: sem issue — commit 973be8d9c, 2026-05-05 12:08 +0200 (Sprint 5 PR H).
 *
 * Contrato: três assinaturas independentes de `lib/firestoreTenantDetail.ts`
 * (`subscribeTenantInvoices`, `subscribeTenantPayments`, `subscribeTenantRefunds`), cada uma
 * com estado próprio — uma falhar não derruba as outras. O `.slice(0, 10)` é só de exibição;
 * o limite da query mora nos subscribers. Valores em CENTAVOS via `MoneyDisplay`. Só leitura.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (web `apps/admin`). A aba homônima do CRM
 * (`apps/crm/src/features/customers/tabs/FinanceTab.tsx`) é outra implementação, com escopo
 * no servidor.
 *
 * Dados: `tenants/{tenantId}/invoices|payments|refunds` — `InvoiceSchema`, `PaymentSchema`,
 * `RefundSchema` em `packages/types/src/schemas.ts`; ordenação por `issued_at`, `created_at` e
 * `requested_at` (desc) nos subscribers. Leitura do super-admin pelas rules do tenant
 * (`match /tenants/{tenantId}/{invoices|payments|refunds}/…` em `firebase/firestore.rules`).
 *
 * Histórico:
 *  - 2026-06-08 22:57 +0200 — (39-F) migração para Cloudscape.
 */
export function TenantFinanceTab({ tenantId }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;
  const [invoices, setInvoices] = useState<InvoiceListResult>({ kind: 'LOADING' });
  const [payments, setPayments] = useState<PaymentListResult>({ kind: 'LOADING' });
  const [refunds, setRefunds] = useState<RefundListResult>({ kind: 'LOADING' });

  useEffect(() => {
    setInvoices({ kind: 'LOADING' });
    return subscribeTenantInvoices(tenantId, setInvoices);
  }, [tenantId]);

  useEffect(() => {
    setPayments({ kind: 'LOADING' });
    return subscribeTenantPayments(tenantId, setPayments);
  }, [tenantId]);

  useEffect(() => {
    setRefunds({ kind: 'LOADING' });
    return subscribeTenantRefunds(tenantId, setRefunds);
  }, [tenantId]);

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">{t('admin.finance.tenantTab.invoicesTitle')}</Header>}>
        {invoices.kind === 'LOADING' && <Spinner />}
        {invoices.kind === 'ERROR' && <Alert type="error">{t('billing.invoices.errorLoad')}</Alert>}
        {invoices.kind === 'READY' && (
          <Table<Invoice>
            items={invoices.invoices.slice(0, 10)}
            trackBy="id"
            variant="embedded"
            empty={<Box textAlign="center" color="inherit">{t('billing.invoices.empty')}</Box>}
            columnDefinitions={[
              {
                id: 'number',
                header: t('billing.invoices.columns.invoiceNumber'),
                cell: (inv): React.ReactNode => {
                  const href = `/billing/invoices/${tenantId}/${inv.id}`;
                  return (
                    <Link
                      href={href}
                      onFollow={(e): void => {
                        e.preventDefault();
                        void navigate(href);
                      }}
                    >
                      <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{inv.invoice_number}</span>
                    </Link>
                  );
                },
              },
              {
                id: 'issuedAt',
                header: t('billing.invoices.columns.issuedAt'),
                cell: (inv): React.ReactNode => formatDate(inv.issued_at, lang),
              },
              {
                id: 'total',
                header: t('billing.invoices.columns.total'),
                cell: (inv): React.ReactNode => (
                  <MoneyDisplay amount={inv.total_amount} currency={inv.currency} locale={lang} />
                ),
              },
              {
                id: 'status',
                header: t('billing.invoices.columns.status'),
                cell: (inv): React.ReactNode => <InvoiceStatusBadge status={inv.status} />,
              },
            ]}
          />
        )}
      </Container>

      <Container header={<Header variant="h2">{t('admin.finance.tenantTab.paymentsTitle')}</Header>}>
        {payments.kind === 'LOADING' && <Spinner />}
        {payments.kind === 'ERROR' && <Alert type="error">{t('billing.payments.errorLoad')}</Alert>}
        {payments.kind === 'READY' && (
          <Table<Payment>
            items={payments.payments.slice(0, 10)}
            trackBy="id"
            variant="embedded"
            empty={<Box textAlign="center" color="inherit">{t('billing.payments.empty')}</Box>}
            columnDefinitions={[
              {
                id: 'paidAt',
                header: t('billing.payments.columns.paidAt'),
                cell: (p): React.ReactNode =>
                  p.paid_at !== null ? formatDateTime(p.paid_at, lang) : t('billing.payments.notPaid'),
              },
              {
                id: 'amount',
                header: t('billing.payments.columns.amount'),
                cell: (p): React.ReactNode => (
                  <MoneyDisplay amount={p.amount} currency={p.currency} locale={lang} />
                ),
              },
              {
                id: 'status',
                header: t('billing.payments.columns.status'),
                cell: (p): React.ReactNode => <PaymentStatusBadge status={p.status} />,
              },
              {
                id: 'provider',
                header: t('billing.payments.columns.provider'),
                cell: (p): React.ReactNode => (
                  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {p.provider ?? t('billing.payments.manual')}
                  </span>
                ),
              },
            ]}
          />
        )}
      </Container>

      <Container header={<Header variant="h2">{t('admin.finance.tenantTab.refundsTitle')}</Header>}>
        {refunds.kind === 'LOADING' && <Spinner />}
        {refunds.kind === 'ERROR' && <Alert type="error">{t('admin.finance.refunds.errorLoad')}</Alert>}
        {refunds.kind === 'READY' && <RefundsTable refunds={refunds.refunds} locale={lang} />}
      </Container>
    </SpaceBetween>
  );
}
